import { useMemo } from "react";

import type { DiscoveryReadinessRuleResult } from "../types/discoveryReadiness";

import { resolveReadinessFixRoute } from "./readinessFixRoutes";

interface ReadinessRulesCardsProps {
  projectId: string;
  rules: DiscoveryReadinessRuleResult[];
}

function formatImpact(weight: number): "High" | "Medium" | "Low" {
  if (weight >= 0.35) return "High";
  if (weight >= 0.2) return "Medium";
  return "Low";
}

export function ReadinessRulesCards({ projectId, rules }: ReadinessRulesCardsProps) {
  const sortedRules = useMemo(() => [...rules].sort((a, b) => (b.weight ?? 0) - (a.weight ?? 0)), [rules]);

  if (!sortedRules.length) {
    return <div className="hud-empty">No readiness rules available.</div>;
  }

  return (
    <div className="hud-rule-grid">
      {sortedRules.map((rule) => {
        const impact = formatImpact(rule.weight ?? 0);
        const fixHref = resolveReadinessFixRoute(projectId, rule.ruleId);
        const scorePercent = Number.isFinite(rule.score) ? Math.round((rule.score ?? 0) * 100) : null;

        return (
          <div key={rule.ruleId} className="hud-mini-card">
            <div className="hud-mini-card__header">
              <div className="hud-label">{impact} • weight {(rule.weight ?? 0).toFixed(2)}</div>
              <span className={`hud-badge ${rule.passed ? "hud-badge--pass" : "hud-badge--fail"}`}>
                {rule.passed ? "PASS" : "FAIL"}
              </span>
            </div>
            <div className="hud-value">{rule.ruleId}</div>
            <div className="hud-subtle">{rule.details || "No description"}</div>

            <div className="hud-mini-card__footer">
              <span className="hud-chip">Score {scorePercent != null ? `${scorePercent}%` : "—"}</span>
              {fixHref ? (
                <a href={fixHref} className="hud-link">
                  Open tab
                </a>
              ) : (
                <span className="hud-subtle">No deep link</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
